import React from 'react';
import { useQuery } from '@apollo/client';

import map from 'lodash.map';

import Error from '@xingternal/pages-warning';
import {
  ModuleHeader,
  MetaData,
  useEditContext,
  usePageContext,
} from '@xingternal/pages-common';
import { getTrackingUrl } from '@xingternal/pages-tracking';

import Skeleton from './Skeleton';

import AboutUs from './AboutUs';

import ABOUT_US_QUERY from '../AboutUsQuery.graphql';
import { DEFAULT_MEDIA_AMOUNT_FOR_ABOUTUS } from '../config';

type AboutUsContainerProps = {
  pageSlug: string;
  companyId?: string;
};
const AboutUsContainer = ({ pageSlug, companyId }: AboutUsContainerProps) => {
  const { data, loading, refetch } = useQuery(ABOUT_US_QUERY, {
    variables: {
      id: pageSlug,
      first: DEFAULT_MEDIA_AMOUNT_FOR_ABOUTUS,
      companyId,
    },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const { isEditing } = useEditContext();
  const { pageContext } = usePageContext();

  const upsellActive = pageContext.upsellActive;
  const hasError = !loading && !data?.pagesAboutUs?.summary;

  const hasSubpage = data?.pagesAboutUs?.properties?.hasSubpage;

  const galleryItemNodes = data?.pagesAboutUs?.media?.edges;

  const galleryItems =
    galleryItemNodes &&
    map(galleryItemNodes, 'node').filter((node: any) => !!node.media);

  const badges = data?.pagesAboutUs?.companyData?.kununuData?.badges || [];

  const availableBadges = badges.filter((badge: any) => badge?.image?.[0]?.url);

  const showMore =
    hasSubpage && !loading && !hasError
      ? {
          to: getTrackingUrl({
            to: `${pageContext?.basePath}/${pageSlug}/about_us`,
            trackingKey: 'learnMoreLink',
          }),
          copyKey: 'EP_ABOUT_US_DISCOVER_CTA',
          testId: 'moreAboutUsLink',
        }
      : null;

  return (
    <>
      <ModuleHeader
        headlineCopyKey="EP_ABOUT_US_HEADLINE"
        showMore={showMore}
      />

      {loading && <Skeleton />}

      {hasError && (
        <div data-testid={'errorContainer'}>
          <Error
            headerText="EP_ERROR_HEADER"
            bodyText="EP_ERROR_BODY"
            buttonText="EP_ERROR_RELOAD_CTA"
            onClick={() => refetch()}
          />
        </div>
      )}

      {data && !loading && !hasError && (
        <>
          <MetaData description={data?.pagesAboutUs?.summary} />
          <AboutUs
            pageSlug={pageSlug}
            upsellActive={upsellActive}
            headline={data?.pagesAboutUs?.headline}
            summary={data?.pagesAboutUs?.summary}
            galleryItems={galleryItems}
            badges={availableBadges}
            hasSubpage={hasSubpage}
            isEditing={isEditing}
          />
        </>
      )}
    </>
  );
};

export default AboutUsContainer;
